/**
 * Reconnaître l'échec de chargement d'un morceau de l'application.
 *
 * Après un déploiement, une page ouverte avant continue d'exécuter l'ancien
 * code. Tant qu'elle n'a besoin de rien de nouveau, c'est sans conséquence ;
 * mais si elle doit charger un morceau (import dynamique, composant différé)
 * qui n'existe plus sur le serveur, l'action plante. C'est le SEUL cas où
 * recharger la page est la bonne réponse — et on le fait à ce moment-là,
 * plutôt qu'à chaque mise à jour détectée.
 */

const MOTIFS = [
  /ChunkLoadError/i,
  /Loading (?:CSS )?chunk [\w-]+ failed/i, // webpack
  /Failed to load chunk/i, // Turbopack
  /Failed to fetch dynamically imported module/i, // Chrome
  /Importing a module script failed/i, // Safari
  /error loading dynamically imported module/i, // Firefox
];

function texteDe(e: unknown): string {
  if (e instanceof Error) return `${e.name}: ${e.message}`;
  if (typeof e === "string") return e;
  if (e && typeof e === "object" && "message" in e) return String((e as { message: unknown }).message);
  return "";
}

export function estErreurDeChunk(e: unknown): boolean {
  const texte = texteDe(e);
  return texte !== "" && MOTIFS.some((m) => m.test(texte));
}

/**
 * Pendant cette fenêtre, pas de second rechargement : si le morceau manque
 * encore juste après, recharger en boucle n'y changera rien.
 */
export const FENETRE_MS = 60_000;

/** `dernier` : horodatage du dernier rechargement de ce type, ou null. */
export function peutRecharger(maintenant: number, dernier: number | null): boolean {
  return dernier == null || !Number.isFinite(dernier) || maintenant - dernier > FENETRE_MS;
}

const CLE = "gestic-rechargement-chunk";

/**
 * Recharge la page, une fois. Renvoie false si on vient déjà de le faire, si
 * l'on est hors ligne (le rechargement n'afficherait que la page hors ligne),
 * ou si le stockage de session est indisponible — sans lui, rien ne
 * garantirait qu'on ne boucle pas.
 */
export function rechargerApresErreurDeChunk(): boolean {
  if (typeof window === "undefined") return false;
  if (navigator.onLine === false) return false;
  let dernier: number | null;
  try {
    const v = sessionStorage.getItem(CLE);
    dernier = v == null ? null : Number(v);
  } catch {
    return false;
  }
  const maintenant = Date.now();
  if (!peutRecharger(maintenant, dernier)) return false;
  try {
    sessionStorage.setItem(CLE, String(maintenant));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}
